/**
 * Offline TTS Fallback — Uses Web Audio API to generate beep tones
 * when Web Speech API is unavailable offline.
 * Provides audio cues for turns (left/right/arrive) even without TTS.
 */

let audioCtx = null;

function getAudioContext() {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioCtx;
}

// Generate a tone sequence for navigation cues
function playTone(freq, duration, count = 1) {
  try {
    const ctx = getAudioContext();
    for (let i = 0; i < count; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime + i * (duration + 0.1));
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * (duration + 0.1) + duration);
      osc.start(ctx.currentTime + i * (duration + 0.1));
      osc.stop(ctx.currentTime + i * (duration + 0.1) + duration);
    }
  } catch {}
}

const TURN_CUES = {
  'left': () => playTone(440, 0.2, 2),       // Two low beeps = turn left
  'slight-left': () => playTone(440, 0.15, 1), // One low beep = slight left
  'right': () => playTone(660, 0.2, 2),       // Two high beeps = turn right
  'slight-right': () => playTone(660, 0.15, 1),// One high beep = slight right
  'sharp-left': () => playTone(440, 0.3, 3),  // Three low beeps = sharp left
  'sharp-right': () => playTone(660, 0.3, 3), // Three high beeps = sharp right
  'straight': () => {},                         // No sound for straight
  'arrive': () => playTone(880, 0.3, 3),      // Three ascending beeps = arrived
  'depart': () => playTone(523, 0.4, 1),      // Single tone = starting
};

/**
 * Check if Web Speech API TTS is available (even offline).
 */
export function isTTSAvailable() {
  try {
    if (!window.speechSynthesis) return false;
    // Try to speak a silent utterance to test
    const voices = window.speechSynthesis.getVoices();
    return voices.length > 0;
  } catch {
    return false;
  }
}

/**
 * Speak text with fallback to audio cues.
 * @param {string} text - The instruction text
 * @param {string} turnType - Turn type for audio cue fallback
 * @param {boolean} voiceOn - Whether voice is enabled
 */
export function speakWithFallback(text, turnType = 'straight', voiceOn = true) {
  if (!voiceOn) { console.log('[TTS] voiceOn=false, skipping:', text?.slice(0, 40)); return; }
  if (!text) { console.log('[TTS] empty text, skipping'); return; }

  console.log('[TTS] Speaking:', text.slice(0, 60), '| turnType:', turnType);

  // Try Web Speech API first
  try {
    if (window.speechSynthesis) {
      // Resume audio context if suspended (required after user interaction on mobile)
      if (window.speechSynthesis.paused) window.speechSynthesis.resume();
      window.speechSynthesis.cancel();

      // Chrome mobile bug: cancel() + speak() in same tick silently fails.
      // Small delay ensures the engine is ready to accept new utterances.
      const doSpeak = () => {
        const utter = new SpeechSynthesisUtterance(text);
        utter.rate = 1.0;
        utter.pitch = 1;
        utter.volume = 1;
        // Try to pick an English voice
        try {
          const voices = window.speechSynthesis.getVoices();
          const enVoice = voices.find(v => v.lang.startsWith('en'));
          if (enVoice) utter.voice = enVoice;
        } catch {}
        let spoke = false;
        utter.onstart = () => { spoke = true; console.log('[TTS] Speech started'); };
        utter.onend = () => { console.log('[TTS] Speech ended'); };
        utter.onerror = (e) => {
          console.warn('[TTS] Speech error:', e.error);
          if (!spoke) {
            const cue = TURN_CUES[turnType] || TURN_CUES.straight;
            cue();
          }
        };
        window.speechSynthesis.speak(utter);
        // Fallback: if speech doesn't start within 1.5s, play audio cue
        setTimeout(() => {
          if (!spoke && window.speechSynthesis.speaking === false) {
            console.warn('[TTS] Speech did not start after 1.5s, using audio cue');
            const cue = TURN_CUES[turnType] || TURN_CUES.straight;
            cue();
          }
        }, 1500);
      };

      // Delay after cancel to let Chrome's synthesis engine reset
      setTimeout(doSpeak, 80);
      return;
    }
  } catch (e) {
    console.warn('[TTS] speechSynthesis error:', e);
  }

  // Direct audio cue fallback
  console.log('[TTS] No speechSynthesis, using audio cue');
  const cue = TURN_CUES[turnType] || TURN_CUES.straight;
  cue();
}

export default { isTTSAvailable, speakWithFallback, TURN_CUES };
